import React, { useState, useEffect } from 'react';
import { supabase } from '../global/supabase';
import HamburgerMenu from '../global/menu';
import '../global/styles.css';

function DailyDashboardAdmin({ userInfo, isAdmin, onLogout, onNavigate }) {
  const [dashboardData, setDashboardData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [todayDate, setTodayDate] = useState('');

  const loadDashboard = async () => {
    setLoading(true);
    setDashboardData([]);

    try {
      // TIMEZONE FIX: Get today's date boundaries in Eastern Time (Florida)
      // This ensures Florida users see records created "today" in their timezone
      const nowET = new Date().toLocaleString('en-US', { timeZone: 'America/New_York' });
      const todayET = new Date(nowET);
      todayET.setHours(0, 0, 0, 0);

      // Convert to UTC for Supabase query
      const todayUTC = new Date(todayET.toLocaleString('en-US', { timeZone: 'UTC' }));
      const tomorrowUTC = new Date(todayUTC);
      tomorrowUTC.setDate(tomorrowUTC.getDate() + 1);

      setTodayDate(todayET.toLocaleDateString('en-US', { timeZone: 'America/New_York' }));

      console.log('Daily Dashboard Query:', {
        todayET: todayET.toLocaleString('en-US', { timeZone: 'America/New_York' }),
        todayUTC: todayUTC.toISOString(),
        tomorrowUTC: tomorrowUTC.toISOString()
      });

      // Query all Mayo location tables
      const locationTables = ['mayo_21', 'mayo_22', 'mayo_23', 'mayo_24', 'mayo_25'];

      const promises = locationTables.map(async (table) => {
        const { data, error } = await supabase
          .from(table)
          .select('*')
          .gte('created_at', todayUTC.toISOString())
          .lt('created_at', tomorrowUTC.toISOString())
          .not('first_name', 'is', null)
          .not('last_name', 'is', null)
          .order('created_at', { ascending: true });

        if (error) {
          console.error(`Error querying ${table}:`, error);
          return [];
        }

        return (data || []).map(record => ({
          ...record,
          sourceTable: table
        }));
      });

      const results = await Promise.all(promises);
      const allLogs = results.flat();

      // Format data for display
      const formattedData = allLogs.map(record => {
        const recordDate = new Date(record.created_at);

        // Extract narcotics with quantities
        const narcotics = {
          fentanyl: parseFloat(record.fentanyl || 0),
          ketamine: parseFloat(record.ketamine || 0),
          midazolam10mg_2ml: parseFloat(record.midazolam10mg_2ml || 0),
          midazolam5mg_5ml: parseFloat(record.midazolam5mg_5ml || 0)
        };

        // Format as "(qty) narcotic"
        const narcoticsTypes = Object.entries(narcotics)
          .filter(([_, qty]) => qty > 0)
          .map(([type, qty]) => `(${qty}) ${formatNarcotic(type)}`)
          .join(', ');

        return {
          ambulance: record.location || 'Unknown',
          logDateTime: recordDate.toLocaleString('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }),
          loggedBy: `${record.last_name || ''} ${record.first_name || ''}`.trim(),
          narcoticsTypes: narcoticsTypes || 'None',
          timestamp: recordDate.getTime()
        };
      });

      // Sort chronologically
      formattedData.sort((a, b) => a.timestamp - b.timestamp);

      setDashboardData(formattedData);
    } catch (error) {
      console.error('Error loading daily dashboard:', error);
      alert('Error loading dashboard data.');
    } finally {
      setLoading(false);
    }
  };

  const formatNarcotic = (key) => {
    return key
      .replace(/_/g, ' ')
      .replace(/(\w+)/g, word =>
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      );
  };

  useEffect(() => {
    if (isAdmin) {
      loadDashboard();
    }
  }, [isAdmin]);

  // Admin access control
  if (!isAdmin) {
    return (
      <div className="container-center">
        <div className="card card-center">
          <h1 className="title">Access Denied</h1>
          <p className="subtitle">You do not have administrator privileges.</p>
          <button onClick={() => onNavigate('home')} className="btn btn-primary">
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      <HamburgerMenu isAdmin={isAdmin} onLogout={onLogout} onNavigate={onNavigate} />

      <div style={{ padding: '80px 20px 20px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Back Button */}
        <button
          onClick={() => onNavigate('admin-dashboard')}
          style={{
            padding: '8px 16px',
            border: '2px solid #0E3293',
            borderRadius: '6px',
            background: 'white',
            color: '#0E3293',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            marginBottom: '20px'
          }}
        >
          ← Back to Admin
        </button>

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #0E3293 0%, #1A4DBB 100%)',
          padding: '30px',
          borderRadius: '12px',
          marginBottom: '20px',
          color: '#fff',
          boxShadow: '0 4px 12px rgba(14, 50, 147, 0.15)'
        }}>
          <h1 style={{ margin: '0 0 10px 0', fontSize: '28px' }}>Admin Dashboard - Daily Snapshot</h1>
          <p style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: 0.9 }}>
            Quick overview of daily inventory log activities for all ambulances
          </p>
          <p style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>
            Date: {todayDate}
          </p>
        </div>

        {/* Refresh Button */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <button
            onClick={loadDashboard}
            className="btn btn-primary"
            style={{
              padding: '12px 30px',
              fontSize: '16px',
              background: '#0E3293',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              transition: 'all 0.3s'
            }}
            onMouseOver={(e) => e.target.style.background = '#1A4DBB'}
            onMouseOut={(e) => e.target.style.background = '#0E3293'}
          >
            Refresh Data
          </button>
        </div>

        {/* Loading Indicator */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#0E3293' }}>
            <div style={{ fontSize: '18px', marginBottom: '10px' }}>Loading Dashboard...</div>
            <div style={{ fontSize: '14px', color: '#666' }}>This may take up to 2 minutes</div>
          </div>
        )}

        {/* Table */}
        {!loading && (
          <div className="report-table-container">
            <div className="table-scroll">
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                background: '#fff',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <thead>
                  <tr style={{ background: '#0E3293', color: '#fff' }}>
                    <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', minWidth: '120px' }}>
                      Ambulance
                    </th>
                    <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', minWidth: '150px' }}>
                      Log Date/Time
                    </th>
                    <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', minWidth: '150px' }}>
                      Logged By
                    </th>
                    <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', minWidth: '250px' }}>
                      Narcotics Types
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.length === 0 ? (
                    <tr>
                      <td colSpan="4" style={{
                        textAlign: 'center',
                        padding: '40px',
                        color: '#666'
                      }}>
                        No logs found for today.
                      </td>
                    </tr>
                  ) : (
                    dashboardData.map((item, index) => (
                      <tr key={index} style={{
                        borderBottom: '1px solid #e0e0e0',
                        transition: 'background 0.2s'
                      }}>
                        <td style={{ padding: '12px 15px' }}>{item.ambulance}</td>
                        <td style={{ padding: '12px 15px' }}>{item.logDateTime}</td>
                        <td style={{ padding: '12px 15px' }}>{item.loggedBy}</td>
                        <td style={{ padding: '12px 15px', fontSize: '14px' }}>{item.narcoticsTypes}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <footer className="footer">
        <p className="text-small">
          Daily Dashboard - NarcoticTrack © 2025 NarcoticTrack Inventory System, LLC
        </p>
      </footer>
    </div>
  );
}

export default DailyDashboardAdmin;
